import { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import { useRfidCategoryStore } from "./store/useRfidCategoryStore";

bouncy.register();

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const CATEGORY_FORBIDDEN_CHARS_PATTERN = /[@#$%^&*<>]/;
const CATEGORY_ONLY_NUMBERS_PATTERN = /^[0-9\s]+$/;
const CATEGORY_NAME_PATTERN = /^[a-zA-Z0-9\s\-]+$/;

const AddEditRfidCategory = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
}) => {
  const { rfidCategories, assetTypes, isSaving, saveRfidCategory, fetchAssetTypes } =
    useRfidCategoryStore();

  useEffect(() => {
    if (showModal && !assetTypes.length) {
      fetchAssetTypes();
    }
  }, [showModal, assetTypes.length, fetchAssetTypes]);

  const handleClose = () => {
    setShowModal(false);
  };

  const initialValues = {
    rfidTypeId: isEditMode
      ? editRow?.rfidTypeId?.toString() || editRow?.assetTypeId?.toString() || ""
      : "",
    categoryName: isEditMode
      ? editRow?.categoryName || editRow?.assetCategoryName || ""
      : "",
    isActive: isEditMode ? editRow?.isActive?.toString() || "true" : "true",
  };

  const validationSchema = Yup.object().shape({
    rfidTypeId: Yup.string().required("Asset Type is Required"),
    categoryName: Yup.string()
      .trim()
      .required("Asset Category is Required")
      .min(3, "Minimum 3 characters required.")
      .max(50, "Maximum 50 characters allowed.")
      .test(
        "not-only-numbers",
        "Asset Category Name cannot contain only numbers.",
        (value) => !value || !CATEGORY_ONLY_NUMBERS_PATTERN.test(value)
      )
      .test(
        "no-special-chars",
        "Special characters such as @, #, $, %, ^, &, *, <, > shall not be allowed.",
        (value) =>
          !value ||
          (!CATEGORY_FORBIDDEN_CHARS_PATTERN.test(value) &&
            CATEGORY_NAME_PATTERN.test(value))
      )
      .test(
        "unique-category",
        "Asset category name already exists for this asset type",
        function (value) {
          const { rfidTypeId } = this.parent;
          if (!value || !rfidTypeId) return true;
          const trimmedName = value.trim();
          return !rfidCategories.some(
            (item) =>
              (item.categoryName?.toLowerCase() === trimmedName.toLowerCase() ||
                item.assetCategoryName?.toLowerCase() === trimmedName.toLowerCase()) &&
              (Number(item.rfidTypeId) === Number(rfidTypeId) ||
                Number(item.assetTypeId) === Number(rfidTypeId)) &&
              item.rfidCategoryId !== editRow?.rfidCategoryId &&
              item.assetCategoryId !== editRow?.assetCategoryId
          );
        }
      ),
    isActive: Yup.string().required("Status is Required"),
  });

  const handleSubmit = async (values) => {
    const requestPayload = {
      rfidTypeId: Number(values.rfidTypeId),
      categoryName: values.categoryName.trim(),
      isActive: values.isActive === "true",
    };

    if (isEditMode) {
      requestPayload.rfidCategoryId =
        editRow.rfidCategoryId || editRow.assetCategoryId;
    }

    const result = await saveRfidCategory(requestPayload, isEditMode);

    if (result?.success !== false) {
      handleClose();
    }
  };

  return (
    <Modal
      size="md"
      show={showModal}
      onHide={handleClose}
      aria-labelledby="rfid-category-modal-title"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title id="rfid-category-modal-title">
          {isEditMode ? "Edit Asset Category" : "Add Asset Category"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
        ></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="row">
                <div className="col-md-6 mb-2">
                  <div className="form-group">
                    <label htmlFor="rfidTypeId" className="form-label">
                     Asset Type <span style={{ color: "#EC1117" }}> *</span>
                    </label>
                    <Select
                      inputId="rfidTypeId"
                      name="rfidTypeId"
                      classNamePrefix="react-select"
                      options={assetTypes.map((type) => ({
                        value: String(type.assetTypeId),
                        label: type.assetTypeName,
                      }))}
                      value={
                        assetTypes
                          .map((type) => ({
                            value: String(type.assetTypeId),
                            label: type.assetTypeName,
                          }))
                          .find(
                            (option) => option.value === String(values.rfidTypeId)
                          ) || null
                      }
                      onChange={(selected) =>
                        setFieldValue("rfidTypeId", selected ? selected.value : "")
                      }
                      placeholder="Select"
                      isClearable={false}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="rfidTypeId"
                      component="div"
                    />
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="form-group">
                    <label htmlFor="categoryName" className="form-label">
                      Asset Category 
                      <span style={{ color: "#EC1117" }}> *</span>
                    </label>
                    <Field
                      type="text"
                      name="categoryName"
                      id="categoryName"
                      placeholder="Enter Asset Category Name"
                      className="form-control"
                      maxLength={50}
                      onChange={(e) => {
                        const formattedValue = e.target.value
                          .slice(0, 50)
                          .replace(/^\s+/, "")
                          .replace(/\s{2,}/g, " ")
                          .replace(CATEGORY_FORBIDDEN_CHARS_PATTERN, "")
                          .replace(/[^a-zA-Z0-9\s\-]/g, "");
                        setFieldValue("categoryName", formattedValue);
                      }}
                    />
                    <ErrorMessage
                      className="required-message"
                      name="categoryName"
                      component="div"
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <label className="form-label">
                  Status<span style={{ color: "#EC1117" }}> *</span>
                </label>
                <div className="form-group" style={{ display: "flex" }}>
                  <div className="form-check">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="rfidActiveRadio"
                      value="true"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="rfidActiveRadio"
                    >
                      Active
                    </label>
                  </div>
                  <div className="form-check" style={{ marginLeft: "40px" }}>
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="rfidInactiveRadio"
                      value="false"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="rfidInactiveRadio"
                    >
                      InActive
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  className="required-message"
                  name="isActive"
                  component="div"
                />
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  style={cancelButtonStyle}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button type="submit" style={saveButtonStyle} disabled={isSaving}>
                  {isSaving ? (
                    <l-bouncy size="22" speed="1.75" color="white"></l-bouncy>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditRfidCategory;
